import {
  Box,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  NoDataSkeleton,
  ActionIconButton,
} from '../../../../common/components';
import { getLotReferenceLabel } from '../utils/lotsUtils';
import { formatDate } from '../../../../utils/formatUtils';
import {
  getLotSubscribersSummary,
  getLotResolvedProject,
  getLotResolvedContractNumber,
  getLotResolvedConstructionType,
} from '../utils/lotExports';

const LotsTableView = ({
  data,
  isLoading,
  onEdit,
  onDelete,
  onDownloadPdf,
  LoadingComponent,
  projectsMap,
  contracts = [],
  constructionTypes = [],
  canEditLots = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const contractsById = Array.isArray(contracts)
    ? contracts.reduce((acc, contract) => {
        if (contract?.uuid) {
          acc[contract.uuid] = contract;
        }
        return acc;
      }, {})
    : {};
  const constructionTypeNamesById = Array.isArray(constructionTypes)
    ? constructionTypes.reduce((acc, item) => {
        if (item?.uuid) {
          acc[item.uuid] = item.name || '-';
        }
        return acc;
      }, {})
    : {};

  const renderSubscribers = (lot) => {
    const summary = getLotSubscribersSummary(lot, contractsById);
    if (summary.firstLabel === '-') {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    if (!summary.extraCount) {
      return <Typography variant="body2">{summary.firstLabel}</Typography>;
    }

    return (
      <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{summary.tooltipText}</div>} arrow>
        <Typography variant="body2" sx={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
          {summary.firstLabel}
          <Box
            component="span"
            sx={{
              ml: 1,
              bgcolor: 'primary.main',
              color: '#fff',
              borderRadius: '12px',
              px: 0.8,
              fontSize: '0.75rem',
              fontWeight: 'bold',
            }}
          >
            +{summary.extraCount}
          </Box>
        </Typography>
      </Tooltip>
    );
  };

  const getProject = (lot) => getLotResolvedProject(lot, projectsMap);
  const getConstructionType = (lot) => getLotResolvedConstructionType(lot, constructionTypeNamesById);
  const getContractNumber = (lot) => getLotResolvedContractNumber(lot, contractsById);
  const getLotIdentifier = (lot) => `${lot?.block_number || '-'} - ${lot?.lot_number || '-'}`;
  const getReferenceState = (lot) => getLotReferenceLabel({ lot, project: getProject(lot) });

  if (isMobile) {
    return (
      <GeneralTableBox>
        {isLoading ? (
          <LoadingComponent />
        ) : data && data.length > 0 ? (
          <Box sx={{ p: 2 }}>
            {data.map((lot) => (
              <Box
                key={lot.uuid}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2,
                  backgroundColor: 'white',
                }}
              >
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                  <Box>
                    <strong>ID:</strong> {getLotIdentifier(lot)}
                  </Box>
                  <Box>
                    <strong>Tipo de construcción:</strong> {getConstructionType(lot)}
                  </Box>
                  <Box>
                    <strong>Inicio obra:</strong> {formatDate(getProject(lot)?.start_date, { fallback: '-' })}
                  </Box>
                  <Box>
                    <strong>Fin obra:</strong> {formatDate(getProject(lot)?.end_date, { fallback: '-' })}
                  </Box>
                  <Box>
                    <strong>Fecha entrega:</strong> {formatDate(lot?.estimated_delivery_date, { fallback: '-' })}
                  </Box>
                  <Box>
                    <strong>Contrato:</strong> {getContractNumber(lot)}
                  </Box>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <strong>Suscriptores:</strong> {renderSubscribers(lot)}
                  </Box>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <strong>Estado:</strong> {getReferenceState(lot)}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <ActionIconButton
                    variant="download"
                    onClick={() => onDownloadPdf?.(lot)}
                  />
                  <ActionIconButton
                    variant="edit"
                    onClick={() => onEdit(lot)}
                    disabled={!canEditLots}
                  />
                  <ActionIconButton
                    variant="delete"
                    onClick={() => onDelete(lot)}
                    disabled={!canEditLots}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <NoDataSkeleton colSpan={1} message="No se encontraron lotes" />
        )}
      </GeneralTableBox>
    );
  }

  return (
    <GeneralTableBox>
      <Table aria-label="lots table" sx={{ minWidth: 1900 }}>
        <TableHead>
          <TableRow>
            <HeadTableCell wmd="12%">ID</HeadTableCell>
            <HeadTableCell wmd="14%">Tipo de construcción</HeadTableCell>
            <HeadTableCell wmd="11%">Inicio obra</HeadTableCell>
            <HeadTableCell wmd="11%">Fin obra</HeadTableCell>
            <HeadTableCell wmd="11%">Fecha entrega</HeadTableCell>
            <HeadTableCell wmd="8%">Contrato</HeadTableCell>
            <HeadTableCell wmd="15%">Suscriptores</HeadTableCell>
            <HeadTableCell wmd="10%">Estado</HeadTableCell>
            <HeadTableCell wmd="8%">Acciones</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <LoadingComponent />
          ) : data && data.length > 0 ? (
            data.map((lot) => (
              <TableRow key={lot.uuid}>
                <RowTableCell>{getLotIdentifier(lot)}</RowTableCell>
                <RowTableCell>{getConstructionType(lot)}</RowTableCell>
                <RowTableCell>{formatDate(getProject(lot)?.start_date, { fallback: '-' })}</RowTableCell>
                <RowTableCell>{formatDate(getProject(lot)?.end_date, { fallback: '-' })}</RowTableCell>
                <RowTableCell>{formatDate(lot?.estimated_delivery_date, { fallback: '-' })}</RowTableCell>
                <RowTableCell>{getContractNumber(lot)}</RowTableCell>
                <RowTableCell>{renderSubscribers(lot)}</RowTableCell>
                <RowTableCell>{getReferenceState(lot)}</RowTableCell>
                <RowTableCell sx={{ textAlign: 'center' }}>
                  <ActionIconButton
                    variant="download"
                    onClick={() => onDownloadPdf?.(lot)}
                  />
                  <ActionIconButton
                    variant="edit"
                    onClick={() => onEdit(lot)}
                    disabled={!canEditLots}
                  />
                  <ActionIconButton
                    variant="delete"
                    onClick={() => onDelete(lot)}
                    disabled={!canEditLots}
                  />
                </RowTableCell>
              </TableRow>
            ))
          ) : (
            <NoDataSkeleton colSpan={9} message="No se encontraron lotes" />
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default LotsTableView;
